import React, { useState, useEffect, useMemo } from 'react'
import {
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow as MUITableRow,
  TablePagination,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'

import { usePickupContext } from '../context/PickupContext'
import TableRow from './TableRow'
import TableHeadCell from './TableHeadCell'
import { tableHeaders } from './constants'
import { StyledHeadRow } from './style'
import {
  parseBriefTableData,
  sortPickupBriefRows,
  getFilteredBriefRows,
  downloadPickupBriefCSV,
} from './helpers'

const PickupBrief = ({ missions, givenOutCodes = [], isFetching }) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [currentPageRows, setCurrentPageRows] = useState([])
  const [rows, setRows] = useState([])
  const history = useHistory()

  const {
    pickupContextState: {
      selectedBrandAmbassadors,
      selectedCampaigns,
      selectedDays,
      selectedWeeks,
      selectedPreset,
      exportBrief,
    },
    setExportBrief,
    setSelectedBrandAmbassadors,
    setSelectedDays,
    setSelectedWeeks,
    setSelectedPreset,
  } = usePickupContext()

  const pickupBriefParsedData = useMemo(
    () => parseBriefTableData(missions, givenOutCodes),
    [missions, givenOutCodes],
  )

  useEffect(() => {
    const filteredRows = getFilteredBriefRows(
      pickupBriefParsedData,
      selectedBrandAmbassadors,
      selectedCampaigns,
      selectedDays,
      selectedWeeks,
      selectedPreset,
    )
    setPage(0)
    setRows(filteredRows)
    setCurrentPageRows(
      filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    )
  }, [
    pickupBriefParsedData,
    selectedBrandAmbassadors,
    selectedCampaigns,
    selectedDays,
    selectedWeeks,
    selectedPreset,
  ])

  useEffect(() => {
    if (selectedPreset) {
      window.history.replaceState(null, '', window.location.pathname)
      setSelectedBrandAmbassadors([])
      setSelectedDays([])
      setSelectedWeeks([])
    }
  }, [selectedPreset])

  useEffect(() => {
    if (exportBrief) {
      downloadPickupBriefCSV(pickupBriefParsedData)
      setExportBrief(false)
    }
  }, [exportBrief])

  useEffect(() => {
    if (
      (selectedBrandAmbassadors && selectedBrandAmbassadors.length) ||
      (selectedDays && selectedDays.length) ||
      (selectedWeeks && selectedWeeks.length)
    ) {
      setSelectedPreset(0)
    }
  }, [selectedBrandAmbassadors, selectedDays, selectedWeeks])

  const handlePageChange = (_e, newPage) => {
    setPage(newPage)
    setCurrentPageRows(
      rows.slice(newPage * rowsPerPage, (newPage + 1) * rowsPerPage),
    )
  }

  const handleRowsPerPageChange = e => {
    const newRowsPerPage = e.target.value
    setRowsPerPage(newRowsPerPage)
    setCurrentPageRows(
      rows.slice(page * newRowsPerPage, (page + 1) * newRowsPerPage),
    )
  }

  const handleRequestSort = (type, newOrder) => {
    let orderedRows = sortPickupBriefRows(type, rows)
    let newRows = orderedRows
    if (newOrder !== 'asc') {
      newRows.reverse()
      newRows.forEach(rows => rows.campaigns.reverse())
    }
    setRows(newRows)
    setPage(0)
    setCurrentPageRows(newRows.slice(0 * rowsPerPage, (0 + 1) * rowsPerPage))
  }

  const handleRedirectPickupAll = (selectedCampaign, selectedBA) => {
    const filteredSelectedBrandAmbassadors = (
      selectedBrandAmbassadors || []
    ).filter(ba => ba.id === selectedBA)
    setSelectedBrandAmbassadors(filteredSelectedBrandAmbassadors)
    history.push(
      `${window.location.pathname.replace(
        'brief',
        'all',
      )}?User=${encodeURIComponent(selectedBA)}&Campaign=${encodeURIComponent(
        selectedCampaign,
      )}`,
    )
  }

  return (
    <div>
      <TableContainer>
        <Table>
          <TableHead>
            <StyledHeadRow>
              {tableHeaders.map((headerData, index) => (
                <TableHeadCell
                  key={`header-${index}`}
                  onRequestSort={handleRequestSort}
                  headerData={headerData}
                />
              ))}
            </StyledHeadRow>
          </TableHead>
          <TableBody>
            {isFetching || rows.length === 0 ? (
              <MUITableRow>
                <TableCell colSpan={7} align="center">
                  {isFetching ? 'Loading...' : 'No data'}
                </TableCell>
              </MUITableRow>
            ) : (
              currentPageRows.map((d, index) => {
                return (
                  <TableRow
                    key={`campaign-${index}`}
                    data={d}
                    index={index}
                    onRedirectPickupAll={handleRedirectPickupAll}
                  />
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 50, 100, 200]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
      />
    </div>
  )
}

export default PickupBrief
